import { Fragment } from 'hubspot-prosemirror/pm/model';
import { getLinkText } from '../plugins/link/LinkUtils';
import { schema as defaultSchema } from '../ProsemirrorSchema';
import { isList, isOrderedList } from './listUtils';
import { getMarkRange } from './markUtils';
import { findAllNodes, replaceAllNodes } from './nodeUtils';
import { fontPluginKey } from '../plugins/FontPlugin';
import { formatPersonalizationToken } from './personalizationTokenUtils';
export function transformEmptyParagraphs(editorState) {
  const {
    schema
  } = editorState;
  const isEmptyParagraph = node => node.type === schema.nodes.paragraph && node.content.size === 0;
  return replaceAllNodes(editorState, isEmptyParagraph, (np, tr) => {
    const {
      node,
      pos
    } = np;
    return tr.replaceWith(tr.mapping.map(pos), tr.mapping.map(pos + node.nodeSize), schema.nodes.paragraph.create(null, schema.text('\t')));
  });
}
export function transformHeadingToParagraph(editorState) {
  const {
    schema
  } = editorState;
  const isHeading = node => node.type === schema.nodes.heading;
  return replaceAllNodes(editorState, isHeading, (np, tr) => {
    const {
      node,
      pos
    } = np;
    return tr.replaceWith(tr.mapping.map(pos), tr.mapping.map(pos + node.nodeSize), schema.nodes.paragraph.create(null, schema.text((node === null || node === void 0 ? void 0 : node.textContent) || '\t')));
  });
}
export function transformListItems(editorState) {
  const {
    schema
  } = editorState;
  const isListParagraph = (node, parentNode) => node.type === schema.nodes.paragraph && parentNode.type === schema.nodes.list_item;
  return replaceAllNodes(editorState, node => isList(schema, node), (np, tr) => {
    const pNodes = findAllNodes(np.node, isListParagraph);
    const plainFragment = [];
    pNodes.forEach((pNode, index) => {
      const textPrefix = isOrderedList(schema, np.node) ? schema.text(`${index + 1}. `) : schema.text('- ');
      plainFragment.push(pNode.node.copy(Fragment.from(textPrefix).append(pNode.node.content)));
    });
    return tr.replaceWith(tr.mapping.map(np.pos), tr.mapping.map(np.pos + np.node.nodeSize), plainFragment);
  });
}
export function transformAtMentions(editorState) {
  const {
    schema
  } = editorState;
  const isAtMention = node => node.type === schema.nodes.atmention;
  return replaceAllNodes(editorState, isAtMention, (np, tr) => {
    const {
      node,
      pos
    } = np;
    return tr.insertText(`@${node.attrs.name}`, tr.mapping.map(pos), tr.mapping.map(pos + 1));
  });
}
export function transformImagesToPlaceholders(editorState, options) {
  if (!(options !== null && options !== void 0 && options.allowPlaceholderImages)) return editorState;
  const {
    schema
  } = editorState;
  const isImage = node => node.type === schema.nodes.image;
  return replaceAllNodes(editorState, isImage, (np, tr) => {
    const {
      pos
    } = np;
    return tr.insertText(`<IMG>`, tr.mapping.map(pos), tr.mapping.map(pos + 1));
  });
}
export function transformPersonalizationTokens(editorState, options) {
  if (!(options !== null && options !== void 0 && options.allowPersonalizationTokens)) return editorState;
  const {
    schema
  } = editorState;
  const isPersonalizationToken = node => node.type === schema.nodes.personalization_token;
  return replaceAllNodes(editorState, isPersonalizationToken, (np, tr) => {
    const {
      node,
      pos
    } = np;
    const text = formatPersonalizationToken(node.attrs.tokenKey, node.attrs.defaultTokenValue);
    return tr.insertText(text, tr.mapping.map(pos), tr.mapping.map(pos + 1));
  });
}
export function transformMeetingLinks(editorState) {
  const {
    schema
  } = editorState;
  const isMeetingLink = node => node.type === schema.nodes.meetings;
  return replaceAllNodes(editorState, isMeetingLink, (np, tr) => {
    const {
      node,
      pos
    } = np;
    return tr.insertText(`${node.attrs.meetingName} (${node.attrs.meetingURL})`, tr.mapping.map(pos), tr.mapping.map(pos + 1));
  });
}
function isLinkTextSameAsHref(linkText, href) {
  if (`mailto:${linkText}` === href) return true;
  if (`http://${linkText}` === href || `https://${linkText}` === href) {
    return true;
  }
  try {
    const url1 = new URL(linkText);
    const url2 = new URL(href);
    return url1.href === url2.href;
  } catch (e) {
    return false;
  }
}
export function transformLinks(editorState) {
  const {
    schema
  } = editorState;
  const isLink = node => {
    const linkMark = schema.marks.link;
    return !!linkMark.isInSet(node.marks);
  };
  return replaceAllNodes(editorState, isLink, (np, tr) => {
    const {
      node,
      pos
    } = np;
    const linkMark = schema.marks.link;
    const mark = linkMark.isInSet(node.marks);
    if (mark) {
      const markBounds = getMarkRange(tr.doc.resolve(tr.mapping.map(pos)), mark);
      const linkText = getLinkText(tr.doc, markBounds);
      const isTextSameAsHref = isLinkTextSameAsHref(linkText, mark.attrs.href);
      tr = tr.removeMark(markBounds.from, markBounds.to, schema.marks.link).insertText(isTextSameAsHref ? linkText : `${linkText} (${mark.attrs.href})`, markBounds.from, markBounds.to);
    }
    return tr;
  });
}
export function transformHardBreak(editorState) {
  const {
    schema
  } = editorState;
  const isHardBreak = node => node.type === schema.nodes.hard_break;
  return replaceAllNodes(editorState, isHardBreak, (np, tr) => {
    const {
      node,
      pos
    } = np;
    return tr.replaceWith(tr.mapping.map(pos), tr.mapping.map(pos + node.nodeSize), schema.text('\n'));
  });
}
export function removeAllMarks(editorState, options) {
  const {
    schema
  } = editorState;
  const markAllowlist = new Set(options && options.allowBasicFormatting ? [schema.marks.strong, schema.marks.em] : []);
  let tempState = editorState;
  for (const markName in schema.marks) {
    if (!markAllowlist.has(schema.marks[markName])) {
      tempState = tempState.apply(tempState.tr.removeMark(0, tempState.doc.content.size, schema.marks[markName]).setMeta('addToHistory', false));
    }
  }
  return tempState;
}

// Recursively splice a non-plaintext node's children into their parent while
// normalizing any non-plaintext descendants with the same rules:
//   - text keeps its marks
//   - allowed nodes pass through with content intact
//   - non-plaintext containers are unwrapped so their normalized children
//     splice in as siblings (this is what keeps blockquote <p>a</p><p>b</p>
//     as two paragraphs instead of one flattened 'ab' paragraph)
//   - inline atoms become text, carrying the marks in effect where they sit
//   - other inline content is flattened to marked text (block-only parents
//     such as doc or blockquote cannot hold inline children)
// Returns the normalized block-level children (`nodes`) plus any trailing
// inline run not yet wrapped in a paragraph (`inlineContent`, only non-empty
// when the parent itself is an inline context).
function normalizeNonPlaintextChildren(node, isNonPlaintextNode, paragraphType) {
  const nodes = [];
  let inlineContent = [];
  let sawInline = false;
  const flushInlineContent = () => {
    if (inlineContent.length || sawInline) {
      nodes.push(paragraphType.createAndFill(null, inlineContent));
      inlineContent = [];
      sawInline = false;
    }
  };
  node.forEach(child => {
    if (!isNonPlaintextNode(child)) {
      if (child.isBlock) {
        flushInlineContent();
        // An allowed paragraph can still contain disallowed inline children
        // (e.g. blockquote > p > tag) — top-most collection stopped at the
        // wrapper, so normalize the paragraph's contents with the same rules,
        // preserving the paragraph shell and its own attrs.
        if (child.childCount) {
          const inner = normalizeNonPlaintextChildren(child, isNonPlaintextNode, paragraphType);
          const content = inner.inlineContent.length ? inner.inlineContent : inner.nodes.length ? inner.nodes[0].content : null;
          nodes.push(child.type.create(child.attrs, content, child.marks));
        } else {
          nodes.push(child);
        }
      } else {
        sawInline = true;
        inlineContent.push(child);
      }
      return;
    }
    if (child.isInline) {
      sawInline = true;
      if (child.isAtom) {
        // Leaf: splice its text in place, keeping the atom's own marks so a
        // marked atom preserves its formatting and an unmarked atom after
        // marked text does not inherit it.
        const text = child.textContent;
        if (text) {
          inlineContent.push(child.type.schema.text(text, child.marks));
        }
        // Atom with no text content contributes nothing (matches the old
        // behavior, which replaced it with an empty string).
      } else {
        // Non-plaintext inline container (e.g. personalization_token_passthrough):
        // splice its normalized children into the surrounding inline flow.
        const inner = normalizeNonPlaintextChildren(child, isNonPlaintextNode, paragraphType);
        if (inner.inlineContent.length) {
          inlineContent.push(...inner.inlineContent);
        } else if (inner.nodes.length) {
          inner.nodes[0].forEach(innerChild => {
            inlineContent.push(innerChild);
          });
        }
      }
      return;
    }
    // Non-plaintext block child: recurse so its normalized children splice
    // in as siblings at this level.
    flushInlineContent();
    const inner = normalizeNonPlaintextChildren(child, isNonPlaintextNode, paragraphType);
    if (inner.nodes.length || inner.inlineContent.length) {
      nodes.push(...inner.nodes);
      if (inner.inlineContent.length) {
        nodes.push(paragraphType.createAndFill(null, inner.inlineContent));
      }
    } else {
      // Childless block: keep the previous behavior of one empty paragraph.
      nodes.push(paragraphType.createAndFill());
    }
  });
  flushInlineContent();
  return {
    nodes,
    inlineContent
  };
}
export function removeNonPlaintextNodes(editorState) {
  const {
    schema
  } = editorState;
  const nodeAllowlist = new Set([schema.nodes.doc, schema.nodes.paragraph, schema.nodes.text]);
  const isNonPlaintextNode = node => !nodeAllowlist.has(node.type);

  // Collect only the topmost non-plaintext nodes; returning false from descendants
  // prevents descent into a matched node's children, so nested wrappers like
  // table -> table_row -> table_cell are counted exactly once.
  const topMostNonPlaintext = [];
  editorState.doc.descendants((node, pos, parent) => {
    if (!parent) return true;
    if (isNonPlaintextNode(node)) {
      topMostNonPlaintext.push({
        node,
        pos
      });
      return false;
    }
    return true;
  });
  let transaction = editorState.tr.setMeta('addToHistory', false);
  topMostNonPlaintext.forEach(({
    node,
    pos
  }) => {
    const text = node.textContent;
    const mappedPos = transaction.mapping.map(pos);
    const mappedEnd = transaction.mapping.map(pos + node.nodeSize);
    if (node.isInline) {
      transaction = transaction.insertText(text, mappedPos, mappedEnd);
    } else {
      // Wrapper invariance: normalize the wrapper's children with the same
      // rules used at the top level and splice the resulting fragment in
      // place of the wrapper, so paragraphs, boundaries, and allowed marks
      // survive intact instead of being flattened into one markless
      // paragraph of textContent.
      const {
        nodes
      } = normalizeNonPlaintextChildren(node, isNonPlaintextNode, schema.nodes.paragraph);
      // Accepted limitation: unwrapping containers like `align` can hoist
      // the wrapper's attrs onto its children (ProseMirror attrsOverride),
      // so paragraphs may end up carrying wrapper-derived align/dir attrs.
      // This is accepted because plaintext-mode editors never enable
      // TEXT_ALIGN — allowsAllRichTextFeatures requires the TEXT_ALIGNMENT
      // capability, which forces rich-text mode — so align/dir attrs are
      // out of contract for plaintext output.
      transaction = transaction.replaceWith(mappedPos, mappedEnd, nodes.length ? nodes : schema.nodes.paragraph.createAndFill());
    }
  });
  return editorState.apply(transaction);
}
export function convertTabsToSpaces(text, marks, tabSize = 4) {
  const fontMark = defaultSchema.marks.font_style.isInSet(marks);
  const hasTabSupport = text.includes('\t') && fontMark && fontMark.attrs.style === 'monospace';
  if (!hasTabSupport) return text;
  let newText = '';
  let index = 0;
  for (const char of text) {
    if (char === '\t') {
      // get distance from current index to nearest tab marker of tabSize
      const distFromTab = tabSize * (Math.floor(index / tabSize) + 1) - index;
      newText += ' '.repeat(distFromTab);
      index += distFromTab;
    } else {
      newText += char;
      index++;
    }
  }
  return newText;
}
const removeFontPlugin = state => {
  return state.reconfigure({
    plugins: state.plugins.filter(p => p.spec.key !== fontPluginKey)
  });
};
const plaintextTransformers = [removeFontPlugin, transformAtMentions, transformMeetingLinks, transformLinks, transformHeadingToParagraph, transformEmptyParagraphs, transformListItems, transformHardBreak, removeAllMarks, transformImagesToPlaceholders, transformPersonalizationTokens];
export function convertToPlaintextState(editorState, options, skipEmptyParagraphTransform = false) {
  const transformers = skipEmptyParagraphTransform ? plaintextTransformers.filter(t => t !== transformEmptyParagraphs) : plaintextTransformers;
  let plainState = editorState;
  transformers.forEach(transformer => {
    plainState = transformer(plainState, options);
  });
  return plainState;
}